import React from 'react'
import { NewTrans } from 'hooks/index'
import { useDispatch, useSelector } from 'react-redux'
import {
    selectSidebarStep,
    selectSelectedSubmissionView,
    selectSelectedCampaignSummary,
    selectShowReviewWarningSaas,
} from 'state/review'
import {
    selectCanSeeInWaitingListSubmission,
    selectCanSeeNextBatchStartModal,
    selectCanSeeNextBatchDisplaySoonModal,
} from 'state/profile'
import {
    SUBMISSION_VIEW_ENUM,
    selectBatchSubmissionsList,
    selectIsPendingSubmitSubmissionExist,
} from 'state/submissions'
import { REVIEW_MODAL_TYPE, setReviewModalOpen, setReviewModalType } from 'state/review'
import BannerAlert from 'particles/Alert/BannerAlert'
import Text from 'particles/Text'
import qs from 'qs'
import { SearchParamObject } from 'components/utils/url'

interface WarningContainerProps {
    className?: string
    campaignUuid: string
}

const BatchingWarningContainer: React.FC = () => {
    const dispatch = useDispatch()
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const batchSubmissionList = useSelector(selectBatchSubmissionsList)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const canSeeInWaitingListSubmission = useSelector(selectCanSeeInWaitingListSubmission)
    const canSeeNextBatchStartModal = useSelector(selectCanSeeNextBatchStartModal)
    const canSeeNextBatchDisplaySoonModal = useSelector(selectCanSeeNextBatchDisplaySoonModal)
    const isPendingSubmissionExist = useSelector(selectIsPendingSubmitSubmissionExist)

    const showBatchNowDisplayNote =
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS &&
        (batchSubmissionList.length <= 0 || !isPendingSubmissionExist) &&
        canSeeNextBatchStartModal &&
        campaignSummary &&
        campaignSummary.batching_enable &&
        campaignSummary.total_submission_in_waiting_list != null &&
        campaignSummary.total_submission_in_waiting_list > 0
    const showBatchWillDisplayNote =
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS &&
        (batchSubmissionList.length <= 0 || !isPendingSubmissionExist) &&
        canSeeNextBatchDisplaySoonModal &&
        campaignSummary &&
        campaignSummary.batching_enable &&
        campaignSummary.total_submission_in_waiting_list != null &&
        campaignSummary.total_submission_in_waiting_list > 0
    const showWaitingListWarn =
        batchSubmissionList.length > 0 &&
        !showBatchNowDisplayNote &&
        !showBatchWillDisplayNote &&
        !canSeeInWaitingListSubmission &&
        campaignSummary &&
        campaignSummary.batching_enable &&
        campaignSummary.total_submission_in_waiting_list != null &&
        campaignSummary.total_submission_in_waiting_list > 0

    const handleModalChange = (reviewModalType: REVIEW_MODAL_TYPE): void => {
        dispatch(setReviewModalType(reviewModalType))
        dispatch(setReviewModalOpen(true))
    }

    return (
        <>
            {showWaitingListWarn && (
                <BannerAlert
                    contentType="info"
                    subText={
                        <NewTrans i18nKey={'submission.review.reviewAwaitingPartipostersAlertBanner'}>
                            <Text scale={'bodyM400'} textDisplay="inline" color={'primary'}>
                                Partiposters who join your campaign after you receive the number of submissions needed
                                to fulfill your campaign are now placed in the Waiting List. Review{' '}
                            </Text>
                            <Text scale={'bodyM700'} textDisplay="inline" color={'action'}>
                                {{
                                    waitingListLength:
                                        campaignSummary && campaignSummary.total_submission_in_waiting_list != null
                                            ? campaignSummary.total_content_submissions -
                                              campaignSummary.total_submission_in_waiting_list
                                            : campaignSummary && campaignSummary.total_content_submissions,
                                }}{' '}
                                awaiting Partiposters
                            </Text>
                            <Text scale={'bodyM400'} textDisplay="inline" color={'primary'}>
                                {' '}
                                first before those in the waiting list.{' '}
                            </Text>
                            <Text
                                scale={'bodyM400'}
                                decoration="underline"
                                color={'action'}
                                textDisplay="inline"
                                textCursor="pointer"
                                onClick={(): void => handleModalChange(REVIEW_MODAL_TYPE.BATCH_ABOUT_WAITING_LIST)}
                            >
                                Why?
                            </Text>
                        </NewTrans>
                    }
                    showClose={false}
                />
            )}
            {showBatchNowDisplayNote && (
                <BannerAlert
                    contentType="info"
                    subText={
                        <NewTrans i18nKey={'submission.review.viewNumberOfPartipostersRequiredAlert'}>
                            <Text scale={'bodyM400'} textDisplay="inline" color={'primary'}>
                                You are now viewing the number of Partiposts needed to fulfill your campaign.{' '}
                            </Text>
                            <Text
                                scale={'bodyM400'}
                                decoration="underline"
                                color={'action'}
                                textDisplay="inline"
                                textCursor="pointer"
                                onClick={(): void => handleModalChange(REVIEW_MODAL_TYPE.BATCH_ABOUT_WAITING_LIST)}
                            >
                                Why?
                            </Text>
                        </NewTrans>
                    }
                    showClose={false}
                />
            )}
            {showBatchWillDisplayNote && (
                <BannerAlert
                    contentType="info"
                    subText={
                        <NewTrans i18nKey={'submission.review.submissionsWaitlistToDisplayAlert'}>
                            <Text scale={'bodyM400'} textDisplay="inline" color={'primary'}>
                                Submissions in the waiting list will be displayed soon.{' '}
                            </Text>
                            <Text
                                scale={'bodyM400'}
                                decoration="underline"
                                color={'action'}
                                textDisplay="inline"
                                textCursor="pointer"
                                onClick={(): void =>
                                    handleModalChange(REVIEW_MODAL_TYPE.BATCH_WHEN_WILL_SEE_WAITING_LIST)
                                }
                            >
                                Why?
                            </Text>
                        </NewTrans>
                    }
                    showClose={false}
                />
            )}
        </>
    )
}

const WarningContainer = React.forwardRef<HTMLDivElement, WarningContainerProps>(({ className, campaignUuid }, ref) => {
    const dispatch = useDispatch()
    const sidebarStep = useSelector(selectSidebarStep)
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const showApprovalWarningFromCampaignTeamType = useSelector(selectShowReviewWarningSaas(campaignUuid))
    const params = qs.parse(location.search) as SearchParamObject
    const viewType = params.viewType as SUBMISSION_VIEW_ENUM
    const showApprovalWarning =
        showApprovalWarningFromCampaignTeamType &&
        viewType &&
        [SUBMISSION_VIEW_ENUM.PENDING, SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS].includes(viewType)

    const handleModalChange = (): void => {
        dispatch(setReviewModalType(REVIEW_MODAL_TYPE.BATCH_BUDGET_USED_UP))
        dispatch(setReviewModalOpen(true))
    }

    if (!sidebarStep) {
        return null
    }

    const showBudgetUsedUpWarn =
        campaignSummary &&
        campaignSummary.budget_used > 0 &&
        campaignSummary.budget > 0 &&
        campaignSummary.budget_used >= campaignSummary.budget

    return (
        <div className={className} ref={ref}>
            {/* Hard code english text in this implementation, will update the copy for all language later */}
            {showApprovalWarning && (
                <BannerAlert
                    contentType={'warning'}
                    subText={
                        <Text scale={'bodyM400'} textDisplay="inline" color={'primary'}>
                            You have 7 days to review each submission. Any submissions not reviewed after 7 days will be
                            automatically reviewed.
                        </Text>
                    }
                    showClose={false}
                />
            )}
            {campaignSummary && campaignSummary.batching_enable ? <BatchingWarningContainer /> : null}
            {showBudgetUsedUpWarn && (
                <BannerAlert
                    contentType={'warning'}
                    subText={
                        <NewTrans i18nKey={'warningMessage.businessLogic.campaignBudgetUsedUp'}>
                            <Text scale={'bodyM400'} textDisplay="inline" color={'primary'}>
                                Your campaign budget has been used up. You will not be able to see more submissions.{' '}
                            </Text>
                            <Text
                                scale={'bodyM400'}
                                decoration="underline"
                                color={'action'}
                                textDisplay="inline"
                                textCursor="pointer"
                                onClick={handleModalChange}
                            >
                                What should I do?
                            </Text>
                        </NewTrans>
                    }
                    showClose={false}
                />
            )}
        </div>
    )
})

WarningContainer.displayName = 'WarningContainer'

export default WarningContainer
